/**
 * DocumentView (U15) — renders a frozen `RenderModel` as the client-facing
 * proposal document. Shared by the interactive viewer and the `/print` route
 * (Puppeteer prints this exact markup), and kept visually aligned with the
 * API's built-in print template (U14 print-html.ts).
 *
 * Product law on this surface:
 *  - PRICES only — the render model structurally contains no cost/markup/
 *    margin, and that vocabulary never appears here;
 *  - every disclosure renders its verification label AND boundary note
 *    verbatim — the document never asserts compliance;
 *  - alternates are priced separately from the base total; the interactive
 *    sum is presentational integer-cent addition of already-derived prices.
 */
import React from 'react';
import { BRAND } from '@buildledger/shared';
import type { DisclosureBlock, RenderModel, RenderSection } from '@buildledger/shared';

import type { PrintCertificate } from './api';
import { centsToDecimal, fmtDate, fmtDateTime, fmtMoney, fmtQty, toCents } from './format';
import { Markdown } from './markdown';

export const VERIFICATION_LABELS: Record<DisclosureBlock['verification'], string> = {
  verified: 'Verified content',
  unverified_draft: 'Unverified draft',
  federal_anchor: 'Federal anchor',
};

const NOTE_LABELS: Record<string, string> = {
  inclusion: 'Included in this proposal',
  exclusion: 'Not included',
  clarification: 'Clarifications',
  assumption: 'Assumptions',
};

// -------------------------------------------------------------- disclosures

function Disclosure({ block }: { block: DisclosureBlock }): JSX.Element {
  return (
    <section className="disclosure">
      <div className="disclosure-head">
        <h3>{block.title}</h3>
        <span className={`badge badge-${block.verification}`}>
          {VERIFICATION_LABELS[block.verification]}
        </span>
        <span className="badge badge-plain">{block.jurisdiction}</span>
      </div>
      <Markdown className="disclosure-body" text={block.bodyMd} />
      <p className="boundary-note">{block.boundaryNote}</p>
    </section>
  );
}

function DisclosuresAt({
  document,
  positions,
}: {
  document: RenderModel;
  positions: Array<DisclosureBlock['placement']['position']>;
}): JSX.Element | null {
  const blocks = document.disclosures.filter((d) => positions.includes(d.placement.position));
  if (blocks.length === 0) return null;
  return (
    <>
      {blocks.map((block) => (
        <Disclosure key={block.ruleKey} block={block} />
      ))}
    </>
  );
}

// ------------------------------------------------------------------ pricing

function SectionPricing({ section, detailed }: { section: RenderSection; detailed: boolean }) {
  if (detailed && section.lines !== null) {
    return (
      <table className="price-table">
        <thead>
          <tr>
            <th className="c-desc">{section.title}</th>
            <th className="c-qty">Qty</th>
            <th className="c-unit">Unit</th>
            <th className="c-price">Price</th>
          </tr>
        </thead>
        <tbody>
          {section.lines.map((line, i) => (
            <tr key={i}>
              <td className="c-desc">{line.description}</td>
              <td className="c-qty num">{line.qty === null ? '' : fmtQty(line.qty)}</td>
              <td className="c-unit">{line.unit ?? ''}</td>
              <td className="c-price num">{fmtMoney(line.price)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={3}>Section total{section.kind === 'allowance' ? ' (allowance)' : ''}</td>
            <td className="c-price num">{fmtMoney(section.price)}</td>
          </tr>
        </tfoot>
      </table>
    );
  }
  return (
    <div className="lump-row">
      <span>
        {section.title}
        {section.kind === 'allowance' ? <em className="muted"> (allowance)</em> : null}
      </span>
      <span className="num">{fmtMoney(section.price)}</span>
    </div>
  );
}

export interface AlternatesState {
  selected: ReadonlySet<string>;
  onToggle: (section: RenderSection) => void;
}

function Alternates({
  document,
  alternates,
}: {
  document: RenderModel;
  alternates: AlternatesState | null;
}): JSX.Element | null {
  const list = document.pricing.alternates;
  if (list.length === 0) return null;

  const selectedCents =
    alternates === null
      ? null
      : list
          .filter((alt) => alternates.selected.has(alt.sectionId))
          .reduce<number | null>((sum, alt) => {
            const cents = toCents(alt.price);
            return sum === null || cents === null ? null : sum + cents;
          }, 0);
  const baseCents = toCents(document.pricing.total);

  return (
    <div className="alternates">
      <h3>
        Alternates{' '}
        <span className="muted">priced separately — not included in the total above</span>
      </h3>
      {list.map((alt) => {
        const isSelected = alternates?.selected.has(alt.sectionId) ?? false;
        const row = (
          <>
            <span className="alt-title">
              {alt.title}
              {alt.alternateGroup === null ? null : (
                <em className="muted"> ({alt.alternateGroup} — choose one)</em>
              )}
            </span>
            <span className="num">+ {fmtMoney(alt.price)}</span>
          </>
        );
        if (alternates === null) {
          return (
            <div key={alt.sectionId} className="lump-row">
              {row}
            </div>
          );
        }
        return (
          <label
            key={alt.sectionId}
            className={`alt-row${isSelected ? ' alt-selected' : ''}`}
            data-testid="alternate-option"
          >
            <input
              type="checkbox"
              checked={isSelected}
              onChange={() => alternates.onToggle(alt)}
            />
            {row}
          </label>
        );
      })}
      {alternates !== null && alternates.selected.size > 0 ? (
        <div className="alt-summary">
          <div className="total-row">
            <span>Base total</span>
            <span className="num">{fmtMoney(document.pricing.total)}</span>
          </div>
          <div className="total-row">
            <span>
              Selected alternates ({alternates.selected.size})
            </span>
            <span className="num">
              {selectedCents === null ? '—' : `+ ${fmtMoney(centsToDecimal(selectedCents))}`}
            </span>
          </div>
          <div className="total-row grand">
            <span>Total with selections</span>
            <span className="num">
              {selectedCents === null || baseCents === null
                ? '—'
                : fmtMoney(centsToDecimal(baseCents + selectedCents))}
            </span>
          </div>
        </div>
      ) : null}
    </div>
  );
}

// -------------------------------------------------------------- certificate

export function Certificate({
  document,
  certificate,
}: {
  document: RenderModel;
  certificate: PrintCertificate;
}): JSX.Element {
  const row = (label: string, value: string | null): JSX.Element | null =>
    value === null || value === '' ? null : (
      <div className="cert-row">
        <span className="cert-label">{label}</span>
        <span className="cert-value">{value}</span>
      </div>
    );
  return (
    <section className="certificate">
      <h2>Signature certificate</h2>
      <p className="cert-intro">
        Record of the electronic signature captured for proposal{' '}
        {document.estimateRef.estimateNumber} (version {document.estimateRef.versionNumber}). This
        page is generated from the append-only signature record and is bound to the signed document
        by its SHA-256 content hash.
      </p>
      {certificate.typedSignature === null ? null : (
        <div className="cert-signature">{certificate.typedSignature}</div>
      )}
      {row('Signed by', certificate.signerName)}
      {row('Email', certificate.signerEmail)}
      {row(
        'Consent to electronic records',
        certificate.consentGivenAt === null ? null : fmtDateTime(certificate.consentGivenAt),
      )}
      {row(
        'Intent to sign confirmed',
        certificate.intentConfirmedAt === null ? null : fmtDateTime(certificate.intentConfirmedAt),
      )}
      {row('Signed at', certificate.signedAt === null ? null : fmtDateTime(certificate.signedAt))}
      {row('IP address', certificate.ip)}
      {row('Browser', certificate.userAgent)}
      {row('Document hash (SHA-256)', certificate.documentHash)}
      {row('Estimate version hash (SHA-256)', document.estimateRef.contentHash)}
      {certificate.consentTextSnapshot === null ? null : (
        <div className="cert-consent">
          <h4>Consent language shown to the signer</h4>
          <p>{certificate.consentTextSnapshot}</p>
        </div>
      )}
      <p className="boundary-note">
        This certificate documents signature capture data recorded by {BRAND.name}. It is
        informational tooling, not a legal opinion on the validity or enforceability of any
        signature or agreement.
      </p>
    </section>
  );
}

// ----------------------------------------------------------------- document

export interface DocumentViewProps {
  document: RenderModel;
  /** Inline logo (print sessions only — public tokens cannot read logo files). */
  logoDataUri?: string | null;
  /** Present on signed copies: appends the certificate page. */
  certificate?: PrintCertificate | null;
  /** When provided, alternates become selectable. */
  alternates?: AlternatesState | null;
}

export function DocumentView({
  document,
  logoDataUri = null,
  certificate = null,
  alternates = null,
}: DocumentViewProps): JSX.Element {
  const brand = document.brand;
  const displayName = brand.displayName ?? brand.companyName;
  const detailed = document.pricing.presentation === 'detailed';
  const accentStyle = {
    '--doc-accent': brand.accentColor ?? '#ff8a00',
    '--doc-primary': brand.primaryColor ?? '#17130d',
  } as React.CSSProperties;

  return (
    <article className="doc" style={accentStyle}>
      <header className="masthead">
        <div className="masthead-brand">
          {logoDataUri !== null && document.cover.showLogo ? (
            <img className="logo" src={logoDataUri} alt="" />
          ) : (
            <span className="monogram" aria-hidden>
              {displayName.slice(0, 1).toUpperCase()}
            </span>
          )}
        </div>
        <div className="who">
          <div className="company-name">{displayName}</div>
          {brand.licenseLines.length > 0 ? (
            <div className="licenses">
              {brand.licenseLines.map((l, i) => (
                <span key={i}>
                  {l.jurisdiction} license {l.licenseNumber}
                  {l.classification === null ? '' : ` · ${l.classification}`}
                </span>
              ))}
              <span className="muted">License information as entered by the contractor.</span>
            </div>
          ) : null}
        </div>
      </header>

      <div className="cover-title">
        <h1>{document.cover.title ?? document.estimateRef.title}</h1>
        {document.cover.subtitle === null ? null : (
          <div className="cover-subtitle">{document.cover.subtitle}</div>
        )}
      </div>

      <div className="parties">
        <div className="party">
          <span className="party-label">Prepared for</span>
          <span className="party-name">{document.client.name}</span>
          {document.client.contactName === null ? null : <span>{document.client.contactName}</span>}
          {document.client.siteAddress === null ? null : <span>{document.client.siteAddress}</span>}
        </div>
        <div className="party">
          <span className="party-label">Proposal</span>
          <span className="party-name">{document.estimateRef.estimateNumber}</span>
          <span>Version {document.estimateRef.versionNumber}</span>
          <span>{fmtDate(document.generatedAt)}</span>
          {document.validUntil === null ? null : (
            <span>Valid through {fmtDate(document.validUntil)}</span>
          )}
        </div>
      </div>

      {document.cover.introMd === null ? null : (
        <Markdown className="intro" text={document.cover.introMd} />
      )}
      <DisclosuresAt document={document} positions={['cover']} />

      {document.scopeNarrative === null ? null : (
        <section className="scope">
          <h2>Scope of work</h2>
          <Markdown text={document.scopeNarrative} />
        </section>
      )}
      <DisclosuresAt document={document} positions={['before_pricing']} />

      <section className="pricing">
        <h2>{detailed ? 'Detailed pricing' : 'Investment'}</h2>
        {document.pricing.sections.map((section) => (
          <SectionPricing key={section.sectionId} section={section} detailed={detailed} />
        ))}
        <div className="totals">
          <div className="total-row">
            <span>Subtotal</span>
            <span className="num">{fmtMoney(document.pricing.subtotal)}</span>
          </div>
          {document.pricing.tax === null ? null : (
            <div className="total-row">
              <span>
                {document.pricing.tax.label} ({document.pricing.tax.ratePct}%)
              </span>
              <span className="num">{fmtMoney(document.pricing.tax.amount)}</span>
            </div>
          )}
          <div className="total-row grand">
            <span>Total</span>
            <span className="num">{fmtMoney(document.pricing.total)}</span>
          </div>
        </div>
        <Alternates document={document} alternates={alternates} />
        {document.pricing.allowanceNote === null ? null : (
          <p className="allowance-note">{document.pricing.allowanceNote}</p>
        )}
      </section>

      <DisclosuresAt document={document} positions={['after_pricing']} />

      {document.notes.length > 0 ? (
        <section className="notes">
          <h2>Notes</h2>
          {Object.entries(
            document.notes.reduce<Record<string, string[]>>((acc, note) => {
              (acc[note.kind] ??= []).push(note.body);
              return acc;
            }, {}),
          ).map(([kind, bodies]) => (
            <div className="note-group" key={kind}>
              <h4>{NOTE_LABELS[kind] ?? kind}</h4>
              <ul>
                {bodies.map((body, i) => (
                  <li key={i}>{body}</li>
                ))}
              </ul>
            </div>
          ))}
        </section>
      ) : null}

      <section className="esign">
        <h4>Electronic signature</h4>
        <Markdown text={document.esign.consentText} />
      </section>
      <DisclosuresAt document={document} positions={['with_signature']} />

      {certificate === null ? null : <Certificate document={document} certificate={certificate} />}
      <DisclosuresAt document={document} positions={['footer']} />

      <footer className="doc-footer">
        <span>{brand.footerText ?? ''}</span>
        <span>
          {document.estimateRef.estimateNumber} · v{document.estimateRef.versionNumber} · generated
          with {BRAND.name}
        </span>
      </footer>
    </article>
  );
}
